import {
  BaseResponse,
  ErrorResponse,
  PaginatedParams,
  PostJSON
} from './request';
import useRequestJSON, { RunnerSegment } from './hooks/useRequestJSON';
import { useCallback } from 'react';

export interface IBrand {
  id: number;
  name: string;
  logo: string;
  en?: string;
  enProvider?: string;
  accountId: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
}

interface BrandParams {
  name: string;
  logo?: string;
}

interface EditBrandParams extends BrandParams {
  id: number;
}

interface BrandResponse extends BaseResponse {
  brand: IBrand;
}

interface GetBrandsResponse extends BaseResponse {
  brands: IBrand[];
  total: number;
}

interface GetBrandsFn {
  (params: PaginatedParams): Promise<GetBrandsResponse | ErrorResponse>;
}

// Request functions

export const CreateBrand = (
  params: BrandParams
): Promise<BrandResponse | ErrorResponse> => {
  return PostJSON<BrandResponse>({
    url: '/chat/create_brand/',
    params
  });
};

export const EditBrand = (
  params: EditBrandParams
): Promise<BrandResponse | ErrorResponse> => {
  return PostJSON<BrandResponse>({
    url: '/chat/edit_brand/',
    params
  });
};

export const transformBrands = (
  response: GetBrandsResponse
): Omit<GetBrandsResponse, 'status'> => {
  return {
    brands: response.brands,
    total: response.total
  };
};

export const GetBrands: GetBrandsFn = (params) =>
  PostJSON<GetBrandsResponse, PaginatedParams>({
    url: '/chat/data_brands/',
    params
  });

// Hooks

export const useGetBrands = (
  params?: PaginatedParams
): Omit<GetBrandsResponse, 'status'> &
  RunnerSegment & { isLoading: boolean } => {
  params = params || {
    page: 0,
    limit: 5
  };
  const request = useCallback(
    () => GetBrands(params),
    [params.page, params.limit]
  );
  const { data, run, isLoading } = useRequestJSON<
    GetBrandsResponse,
    Omit<GetBrandsResponse, 'status'>
  >({
    request,
    transformFn: transformBrands
  });
  return {
    isLoading,
    brands: data ? data.brands : undefined,
    total: data ? data.total : undefined,
    run: () => run(request)
  };
};
